import { FC, ReactNode, useCallback, useMemo, useRef, useState } from 'react';

import { ValidationContext, ValidationContextType, ValidationEntry } from './ValidationContextCore';

export const ValidationProvider: FC<{ children: ReactNode }> = ({ children }) => {
	const [entries, setEntries] = useState<Record<string, ValidationEntry>>({});
	const prevResultsRef = useRef<Record<string, string | null>>({});

	const isNextBlockedByValidation = Object.values(entries).some(
		(entry) => entry.result === 'Error' || (entry.result === 'Warning' && !entry.accepted)
	);

	const hasNonCorrectValidation = Object.values(entries).some(
		(entry) => entry.result !== null && entry.result !== 'Correct' && !entry.accepted
	);

	const getAccepted = useCallback(
		(id: string): boolean => {
			return entries[id]?.accepted ?? false;
		},
		[entries]
	);

	const setAccepted = useCallback((id: string, value: boolean) => {
		setEntries((prev) => ({
			...prev,
			[id]: {
				...prev[id],
				accepted: value,
			},
		}));
	}, []);

	const setValidationResult = useCallback((id: string, result: string | null) => {
		if (prevResultsRef.current[id] === result) return;
		prevResultsRef.current[id] = result;

		setEntries((prev) => ({
			...prev,
			[id]: {
				result,
				accepted: false,
			},
		}));
	}, []);

	const unregister = useCallback((id: string) => {
		setEntries((prev) => {
			const next = { ...prev };

			delete next[id];

			return next;
		});
		delete prevResultsRef.current[id];
	}, []);

	const value = useMemo<ValidationContextType>(
		() => ({
			isNextBlockedByValidation,
			hasNonCorrectValidation,
			getAccepted,
			setAccepted,
			setValidationResult,
			unregister,
		}),
		[
			isNextBlockedByValidation,
			hasNonCorrectValidation,
			getAccepted,
			setAccepted,
			setValidationResult,
			unregister,
		]
	);

	return <ValidationContext.Provider value={value}>{children}</ValidationContext.Provider>;
};
